export const WEBINAR_HOST_GUIDE = {
  id: "webinar-host-guide",
  title: "Webinar Host Guide",
  description:
    "Step-by-step guide for hosting webcasts using the Insights Platform and Zoom.",
  icon: "Calendar",
  version: "1.0",
  lastUpdated: "2026-02-15",
  requires: ["zoom"],
  sections: [
    // ── Section 1 ── One-Time Setup ──────────────────────────────────────
    {
      id: "one-time-setup",
      title: "One-Time Setup",
      content: [
        {
          type: "callout",
          variant: "info",
          text: "Admin only — do this once.",
        },

        // 1. Set Up Zoom Account Branding
        {
          type: "heading",
          text: "1. Set Up Zoom Account Branding",
        },
        {
          type: "paragraph",
          text: "These settings apply automatically to every webinar you create — you only need to do this once.",
        },
        {
          type: "steps",
          items: [
            "Open your browser and go to **zoom.us** → sign in",
            "Click **Account Management** in the left sidebar → **Account Settings**",
            "Click the **Webinar** tab at the top",
            "Scroll down to the **Branding** section",
            "Toggle **Session Branding** to **ON** (blue)",
            "Click the **lock icon** next to it so it stays on permanently",
            "Upload your branding assets:\n   - **Wallpaper** — your branded background image (1920 x 1080 pixels, JPEG or PNG, max 15 MB)\n   - **Virtual Backgrounds** — background images for panelists (same specs)\n   - **Name Tags** — choose your brand colors for the panelist name labels",
            "Click **Save**",
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "From now on, every webinar will automatically use this branding. You never need to set it again.",
        },

        // 2. Upload Your Intro Video Template
        {
          type: "heading",
          text: "2. Upload Your Intro Video Template",
        },
        {
          type: "paragraph",
          text: "This is the base video that plays while attendees are waiting. Insights will overlay each webinar's title onto it.",
        },
        {
          type: "steps",
          items: [
            "Log in to the **Insights Platform**",
            "Go to the **Webcasts** page from the left sidebar",
            'Click the **Upload Intro Video** button (top-right area, next to "Create Webcast")',
            "Select your template video file (the 90-second looping video without any title on it)",
            "Wait for the upload to complete — the button will change to **Replace Intro Video**",
          ],
        },
        {
          type: "callout",
          variant: "tip",
          text: "You only need to do this once. You can replace it later if your template changes.",
        },
      ],
    },

    // ── Section 2 ── Before Each Webinar ─────────────────────────────────
    {
      id: "before-each-webinar",
      title: "Before Each Webinar",
      content: [
        // 3. Create the Webcast in the CRM
        {
          type: "heading",
          text: "3. Create the Webcast in Insights",
        },
        {
          type: "steps",
          items: [
            "Go to the **Webcasts** page",
            "Click **Create Webcast**",
            "Fill in:\n   - **Title** — the name of the webinar (this will appear on the intro video)\n   - **Description** — what the webinar is about\n   - **Date & Time** — when it starts\n   - **Duration** — how long it will run (default is 60 minutes)",
            "Click **Create**",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "This automatically creates the webinar in Zoom with all the right settings, including a practice session so you can prepare before going live.",
        },

        // 4. Invite Attendees
        {
          type: "heading",
          text: "4. Invite Attendees",
        },
        {
          type: "steps",
          items: [
            "Click on the webcast you just created to open its details",
            "Click **Invite Contacts**",
            "Select the contacts you want to invite",
            "Click **Send Invitations**",
          ],
        },
        {
          type: "paragraph",
          text: "Each contact receives an email with a link to register for the webinar.",
        },

        // 5. Generate the Intro Video
        {
          type: "heading",
          text: "5. Generate the Intro Video",
        },
        {
          type: "steps",
          items: [
            "In the webcast detail panel, click **Generate Intro Video**",
            "You'll see a preview of your template video with the webinar title overlaid",
            "Click **Generate** — a progress bar will show as it processes (~90 seconds)",
            "When complete, click **Download** to save the video file to your computer",
          ],
        },
        {
          type: "paragraph",
          text: "You now have a branded video with the webinar title ready to play.",
        },
      ],
    },

    // ── Section 3 ── On the Day of the Webinar ──────────────────────────
    {
      id: "on-the-day",
      title: "On the Day of the Webinar",
      content: [
        // 6. Join the Practice Session
        {
          type: "heading",
          text: "6. Join the Practice Session",
        },
        {
          type: "paragraph",
          text: "The practice session starts before any attendees can see or hear you. This is your setup time.",
        },
        {
          type: "steps",
          items: [
            "Go to **zoom.us** → **Webinars** → find your webinar → click **Start**\n   - Alternatively, use the **Start URL** from the webcast detail in Insights",
            "Zoom opens. You are now in the **practice session** — only you and any panelists can see this",
            'Attendees who join early will see a "Please wait, the webinar will begin shortly" message with your branded wallpaper',
          ],
        },

        // 7. Screen-Share the Intro Video
        {
          type: "heading",
          text: "7. Screen-Share the Intro Video",
        },
        {
          type: "paragraph",
          text: "While still in the practice session:",
        },
        {
          type: "steps",
          items: [
            "Open the intro video file you downloaded in step 5\n   - Use any video player (VLC, Windows Media Player, or just double-click the file)\n   - Set the video player to **loop** (repeat)",
            "In Zoom, click the green **Share Screen** button at the bottom",
            "Select the video player window",
            "**Important**: Tick the checkbox **Share sound** (bottom-left of the share dialog)",
            "Click **Share**",
          ],
        },
        {
          type: "callout",
          variant: "info",
          text: "Your branded intro video is now ready. Attendees can't see it yet — you're still in practice mode.",
        },

        // 8. Go Live
        {
          type: "heading",
          text: "8. Go Live",
        },
        {
          type: "paragraph",
          text: "When you're ready to start the webinar:",
        },
        {
          type: "steps",
          items: [
            'Click the **Broadcast** button in Zoom (top banner, usually says "Broadcast to all")',
            "A confirmation dialog appears — click **Broadcast**",
            "Attendees can now see and hear the intro video playing",
            "Let it loop for a minute or two while people join",
          ],
        },

        // 9. Start Presenting
        {
          type: "heading",
          text: "9. Start Presenting",
        },
        {
          type: "paragraph",
          text: "When you're ready to begin the actual presentation:",
        },
        {
          type: "steps",
          items: [
            "Click **Stop Share** at the top of your screen to stop the intro video",
            "Close your video player",
            "You're now live on camera with your branded wallpaper, virtual backgrounds, and name tags all active",
            "Begin your presentation",
          ],
        },
      ],
    },

    // ── Section 4 ── After the Webinar ───────────────────────────────────
    {
      id: "after-the-webinar",
      title: "After the Webinar",
      content: [
        // 10. End the Webinar
        {
          type: "heading",
          text: "10. End the Webinar",
        },
        {
          type: "steps",
          items: [
            "Click **End** in Zoom → **End Webinar for All**",
            "Zoom will automatically save the cloud recording (if enabled)",
          ],
        },

        // 11. Check Attendance (Optional)
        {
          type: "heading",
          text: "11. Check Attendance (Optional)",
        },
        {
          type: "steps",
          items: [
            "Go back to the **Webcasts** page in Insights",
            "Click on the webcast to open its details",
            "Click **Reconcile Attendance**",
            "Insights will show you who attended, who was a no-show, and any walk-ins",
          ],
        },
        {
          type: "callout",
          variant: "warning",
          text: "Zoom's attendance report may take a few minutes to become available after the webinar ends. Wait 5-10 minutes before reconciling.",
        },

        // 12. Access the Recording (Optional)
        {
          type: "heading",
          text: "12. Access the Recording (Optional)",
        },
        {
          type: "steps",
          items: [
            "In the webcast detail panel, click **Recordings**",
            "Recordings are available once Zoom finishes processing them (usually within an hour)",
          ],
        },
      ],
    },

    // ── Section 5 ── Quick Reference Card ────────────────────────────────
    {
      id: "quick-reference-card",
      title: "Quick Reference Card",
      content: [
        {
          type: "table",
          headers: ["When", "What", "Where"],
          rows: [
            [
              "Once ever",
              "Set up Zoom branding (wallpaper, backgrounds, name tags)",
              "zoom.us → Account Settings",
            ],
            [
              "Once ever",
              "Upload intro video template",
              "Insights → Webcasts → Upload Intro Video",
            ],
            [
              "Before each webinar",
              "Create webcast + invite contacts",
              "Insights → Webcasts → Create",
            ],
            [
              "Before each webinar",
              "Generate & download intro video",
              "Insights → Webcast detail → Generate",
            ],
            [
              "Day of webinar",
              "Join practice session",
              "Zoom → Start webinar",
            ],
            [
              "Day of webinar",
              "Screen-share intro video (with sound)",
              "Zoom → Share Screen",
            ],
            [
              "Day of webinar",
              "Click Broadcast to go live",
              "Zoom → Broadcast button",
            ],
            [
              "Day of webinar",
              "Stop Share → start presenting",
              "Zoom → Stop Share",
            ],
            [
              "After webinar",
              "End Webinar for All",
              "Zoom → End",
            ],
            [
              "After webinar",
              "Reconcile attendance",
              "Insights → Webcast detail",
            ],
          ],
        },
      ],
    },

    // ── Section 6 ── Troubleshooting ─────────────────────────────────────
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      content: [
        {
          type: "faq",
          items: [
            {
              question: "I don't see the Broadcast button",
              answer:
                "You may not be in the practice session. Make sure the webinar was created with practice session enabled (Insights does this automatically).",
            },
            {
              question: "Attendees say they can't hear the intro video",
              answer:
                "When sharing your screen, make sure you ticked the **Share sound** checkbox in the share dialog.",
            },
            {
              question: "The intro video has no title on it",
              answer:
                "You may have downloaded the template instead of generating the video. Go to the webcast detail in Insights and click **Generate Intro Video** — this creates a new video with the title overlaid.",
            },
            {
              question: "Zoom says the report isn't available yet",
              answer:
                "Zoom's attendance reports take a few minutes after the webinar ends. Wait 5-10 minutes and try reconciling again.",
            },
            {
              question: "My wallpaper/branding isn't showing",
              answer:
                "Check that Session Branding is enabled in Zoom Account Settings (see step 1). If you just enabled it, you may need to create a new webinar for it to take effect.",
            },
          ],
        },
      ],
    },
  ],
};
